import { useEffect } from 'react';
import { chatSend } from '../../../assets/images/images';
import { formatMessage, parseBulletPoints } from '../../../utility/helpers';
import { getSectionNameToKey, getSectionOptions } from '../../../utility/chatSections';
import { useSelectionState } from '../../../hooks/useSelectionState';
import { useSuggestionState } from '../../../hooks/useSuggestionState';
import OptionButton from './shared/OptionButton';
import MessageContainer from './shared/MessageContainer';
import { IncomingMessageICPProps } from './shared/types';

/**
 * IncomingMessageICP - For ChatWithICP (Ideal Candidate Profile)
 * Features:
 * - Show all options (Must/Should/Good-to-have)
 * - No prioritization
 * - Suggestion handling with section selector
 */
const IncomingMessageICP = ({
    chat,
    message,
    isLoading,
    isFromJob = false,
    setLastMessageId,
    handleSendMessage,
    setEnableGenerateResponse,
    setLastQuestionStep,
    onSelectSuggestion,
    onMoveSuggestions,
    isLastMessage,
}: IncomingMessageICPProps) => {
    const isMultiSelect = chat?.is_multi_select;
    const {
        selectedOptions,
        optionKeys,
        renderKey,
        handleOptionClick,
        setSelectionsFromJSON
    } = useSelectionState(isMultiSelect);

    const {
        selectedSuggestions,
        pendingSuggestions,
        renderKey: suggestionRenderKey,
        handleSuggestionClick,
        setPendingSuggestions,
        setSelectedSuggestions
    } = useSuggestionState();

    useEffect(() => {
        setSelectionsFromJSON(chat?.select_json);
    }, [chat?.select_json, chat?.id, setSelectionsFromJSON]);

    useEffect(() => {
        if (chat.id && !chat?.is_local && typeof chat.id === 'number') {
            setLastMessageId(chat.id);
        }
        if (chat?.step && typeof setLastQuestionStep === 'function') {
            setLastQuestionStep(chat?.step);
        }
        if (chat?.is_finished) {
            setEnableGenerateResponse(chat?.is_finished);
        }
    }, [chat, setLastQuestionStep, setLastMessageId, setEnableGenerateResponse]);

    const handleKeyActivate = (e: React.KeyboardEvent, onActivate: () => void) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onActivate();
        }
    };

    const handleMultiClick = () => {
        if (selectedOptions.length === 0) return;

        if (!isMultiSelect && selectedOptions.length === 1) {
            const option = selectedOptions[0];
            const key = optionKeys.get(option);

            if (key) {
                handleSendMessage({ [key]: option }, option);
            } else {
                handleSendMessage([option], option);
            }
        } else {
            handleSendMessage(selectedOptions, '');
        }
    };

    const renderOptions = () => {
        // Disable options ONLY if it's not the last message.
        // We ignore select_json for the last message to allow users to change their selection
        // or retry if the flow gets stuck.
        const isDisabled = !isLastMessage;

        if (message?.title && !Array.isArray(message?.title) && typeof message?.title === 'object' && message.title !== null) {
            const entries = Object.entries(message.title)
                .filter(([key, value]) => key !== 'could_have')
                .filter(([key, value]) => !(value as string).toLowerCase().includes('what makes you a great candidate'));

            return (
                <>
                    {entries.map(([key, value], index: number) => {
                        const isSelected = selectedOptions.includes(value as string);
                        return (
                            <OptionButton
                                key={`${key}-${index}`}
                                value={value as string}
                                keyProp={key}
                                index={index}
                                isSelected={isSelected}
                                renderKey={renderKey}
                                isDisabled={isDisabled}
                                onClick={handleOptionClick}
                            />
                        );
                    })}
                </>
            );
        } else if (message?.title && Array.isArray(message?.title)) {
            const options = message.title
                .filter((option: string) => option !== 'Could-Haves')
                .filter((option: string) => !option.toLowerCase().includes('what makes you a great candidate'));

            return (
                <>
                    {options.map((option: string, index: number) => {
                        const isSelected = selectedOptions.includes(option);
                        return (
                            <OptionButton
                                key={`${option}-${index}`}
                                value={option}
                                keyProp={null}
                                index={index}
                                isSelected={isSelected}
                                renderKey={renderKey}
                                isDisabled={isDisabled}
                                onClick={handleOptionClick}
                            />
                        );
                    })}
                </>
            );
        }
        return null;
    };

    const handleSendSuggestions = async () => {
        const sectionNameToKey = getSectionNameToKey('icp');
        let sectionLabel = '';
        let sectionKey = '';

        // Try to resolve section from multiple sources
        if (typeof message?.section === 'string') {
            sectionLabel = message.section;
        } else if (typeof message?.title === 'string' && !Array.isArray(message.title)) {
            sectionLabel = message.title;
        } else if (chat?.select_json && typeof chat.select_json === 'object' && !Array.isArray(chat.select_json) && Object.keys(chat.select_json).length > 0) {
            const firstKey = Object.keys(chat.select_json)[0];
            sectionLabel = chat.select_json[firstKey] || firstKey;
        } else if (chat?.payload?.title && typeof chat.payload.title === 'object' && !Array.isArray(chat.payload.title) && Object.keys(chat.payload.title).length > 0) {
            const firstKey = Object.keys(chat.payload.title)[0];
            sectionLabel = chat.payload.title[firstKey] || firstKey;
        }

        if (sectionLabel) {
            sectionKey = sectionNameToKey[sectionLabel];
            if (!sectionKey || sectionKey === sectionLabel) {
                const sectionOptions = getSectionOptions('icp');
                const foundOption = sectionOptions.find(opt =>
                    opt.label === sectionLabel ||
                    opt.key === sectionLabel ||
                    opt.label.toLowerCase() === sectionLabel.toLowerCase()
                );
                sectionKey = foundOption?.key || sectionLabel;
            }
        }

        if (sectionKey && typeof sectionKey === 'string' && sectionKey.length > 0) {
            if (typeof onMoveSuggestions === 'function') {
                await onMoveSuggestions(sectionKey, selectedSuggestions);
            } else if (typeof onSelectSuggestion === 'function') {
                for (const s of selectedSuggestions) {
                    // eslint-disable-next-line no-await-in-loop
                    await onSelectSuggestion(s, sectionKey);
                }
            }
            setSelectedSuggestions([]);
            return;
        }

        // No target section found - show section selector
        setPendingSuggestions(selectedSuggestions);
    };

    const renderSectionSelector = (suggestion: string | string[]) => {
        const sectionOptions = getSectionOptions('icp');
        const handleSectionSelect = async (key: string, label: string) => {
            if (typeof onMoveSuggestions === 'function') {
                const items = Array.isArray(suggestion) ? suggestion : [suggestion];
                await onMoveSuggestions(key, items);
            } else if (onSelectSuggestion) {
                const items = Array.isArray(suggestion) ? suggestion : [suggestion];
                for (const s of items) {
                    // eslint-disable-next-line no-await-in-loop
                    await onSelectSuggestion(s, key);
                }
            }
            setPendingSuggestions(null);
            setSelectedSuggestions([]);
        };

        return (
            <>
                <p className="icp-action-hint">
                    Choose which section you want me to add {Array.isArray(suggestion) && suggestion.length === 1 ? 'this' : 'these'}
                </p>
                <div className="chat-single-option no-image-option">
                    {sectionOptions.map(({ key, label }) => (
                        <div
                            key={key}
                            className={`chat-option`}
                            title={`Add to ${label}`}
                            onClick={() => handleSectionSelect(key, label)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => handleKeyActivate(e, () => handleSectionSelect(key, label))}
                        >
                            <p className="chat-option-des">{label}</p>
                        </div>
                    ))}
                    <div
                        className={`chat-option chat-option-cancel`}
                        title={`Cancel`}
                        onClick={() => setPendingSuggestions(null)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => handleKeyActivate(e, () => setPendingSuggestions(null))}
                    >
                        <p className="chat-option-des">Cancel</p>
                    </div>
                </div>
            </>
        );
    };

    const renderMessageContent = () => {
        if (!message) {
            return <p><span className="inline-bold-text">Hunter: </span></p>;
        }

        if (message?.is_initial) {
            const messageText = message?.message || "Welcome! Let's define your ideal candidate profile.";
            return <p><span className="inline-bold-text">Hunter: </span>{messageText}</p>;
        }

        if (message?.out_of_context === true) {
            const outOfContextMessage = Array.isArray(message?.message)
                ? message.message[0]
                : message?.message || "Sorry, that's out of my context.";

            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {outOfContextMessage}
                </p>
            );
        }

        if (message?.title && (Array.isArray(message?.title) || (typeof message?.title === 'object' && message.title !== null))) {
            const messageText = message?.message ? formatMessage(message.message) : "Please choose from the following options:";
            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {messageText}
                </p>
            );
        }

        // Handle suggestions
        if (message?.message && Array.isArray(message?.message)) {
            const hasTargetSection = (typeof message?.section === 'string') ||
                (typeof message?.title === 'string' && typeof message?.title !== 'object' && !Array.isArray(message?.title));

            // Disable suggestions ONLY if it's not the last message.
            const isSuggestionsDisabled = !isLastMessage;

            return (
                <div>
                    <p>
                        <span className="inline-bold-text">Hunter: </span>
                        {hasTargetSection ? (
                            <>
                                Here are {message.message.length} {message.section} suggestions:
                                <br />
                            </>
                        ) : (
                            <>Here are some suggestions:</>
                        )}
                    </p>
                    <div className="chat-single-option no-image-option" key={`suggestions-${suggestionRenderKey}`}>
                        {message.message
                            .filter((text: any) => !text.toLowerCase().includes('what makes you a great candidate'))
                            .map((text: any, index: any) => {
                                const isSelected = selectedSuggestions.includes(text);
                                return (
                                    <div
                                        key={`${text}-${index}-${suggestionRenderKey}`}
                                        className={`chat-option ${isSelected ? 'selected' : ''} ${isSuggestionsDisabled ? 'disabled' : ''}`}
                                        title={isSuggestionsDisabled ? 'This suggestion has already been selected' : 'Click to select'}
                                        onClick={isSuggestionsDisabled ? undefined : () => handleSuggestionClick(text)}
                                        role="button"
                                        tabIndex={isSuggestionsDisabled ? -1 : 0}
                                        onKeyDown={isSuggestionsDisabled ? undefined : (e) => handleKeyActivate(e, () => handleSuggestionClick(text))}
                                        aria-disabled={isSuggestionsDisabled}
                                    >
                                        <p className="chat-option-des">{text}</p>
                                    </div>
                                );
                            })}
                    </div>
                    {selectedSuggestions.length > 0 && !isSuggestionsDisabled && (
                        <div className="chat-single-option no-image-option">
                            <img
                                src={chatSend}
                                alt="send"
                                className={`chat-send-btn`}
                                onClick={handleSendSuggestions}
                                role="button"
                                aria-label="Send selected suggestions"
                                tabIndex={0}
                                onKeyDown={(e) => handleKeyActivate(e, handleSendSuggestions)}
                            />
                        </div>
                    )}
                    {pendingSuggestions && renderSectionSelector(pendingSuggestions)}
                </div>
            );
        }

        const messageText = formatMessage(message?.message);
        const bulletData = messageText ? parseBulletPoints(messageText) : null;

        if (bulletData?.bulletItems.length) {
            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {bulletData.introText && (
                        <>
                            <span>{bulletData.introText}</span>
                            <br />
                        </>
                    )}
                    <ul className="message-list">
                        {bulletData.bulletItems.map((item: string, index: number) => (
                            <li key={index}>{item}</li>
                        ))}
                    </ul>
                    {bulletData.closingText && (
                        <span>{bulletData.closingText}</span>
                    )}
                </p>
            );
        }

        return <p><span className="inline-bold-text">Hunter: </span>{messageText}</p>;
    };

    return (
        <>
            <MessageContainer isFromJob={isFromJob}>
                {renderMessageContent()}
            </MessageContainer>

            {((Array.isArray(message?.title) && message.title.length > 0) ||
                (!Array.isArray(message?.title) && message?.title && (typeof message?.title === 'object' && message.title !== null))) && (
                    <div className={`chat-single-option no-image-option ${isLoading ? 'p-none' : ''}`}>
                        {renderOptions()}
                        {(!chat?.select_json || isLastMessage) && (
                            <img
                                src={chatSend}
                                alt="send"
                                className={`chat-send-btn ${selectedOptions?.length > 0 ? '' : 'p-none'}`}
                                onClick={handleMultiClick}
                                role="button"
                                aria-label="Send selected options"
                                tabIndex={0}
                                onKeyDown={(e) => handleKeyActivate(e, handleMultiClick)}
                            />
                        )}
                    </div>
                )}
        </>
    );
};

export default IncomingMessageICP;

